/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo } from 'react';
import MainContactsContext from './MainContactsContext';
import initialMainContactsState from './initialMainContactsState';
import userReducer from 'contexts/mainContacts/reducer/mainContacts.reducer';
import * as action from 'contexts/mainContacts/reducer/mainContacts.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { ChildrenProps } from 'interfaces/general.interface';
import { IcreateMainContactAPIResponseBody } from 'api/endpoints/mainContacts/mainContacts.response.interface';
import {
  IremoveContactActionProps,
  IselectContactActionProps,
  IupdateContactActionProps,
} from 'interfaces/mainContacts.interface';
import useContextCompany from 'hooks/contexts/useContextCompany';

function MainContactsProvider(props: ChildrenProps) {
  const [state, dispatch] = useReducer(userReducer, initialMainContactsState);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { t: translate } = useTranslation();
  const { company } = useContextCompany();

  const getMainContacts = useCallback(async () => {
    if (!company?.id || !isAuthenticated) return false;
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.getMainContactsAction({
      dispatch,
      clientId: company.id,
      token,
      translate,
    });
  }, [company?.id, isAuthenticated]);

  const selectContact = useCallback(({ contact }: IselectContactActionProps) => {
    action.selectContactAction({ dispatch, contact });
  }, []);

  const createContact = useCallback(
    async ({ contact }: { contact: IcreateMainContactAPIResponseBody }) => {
      if (!company?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.createContactAction({
        dispatch,
        clientId: company.id,
        contact,
        token,
        translate,
      });
    },
    [company?.id]
  );

  const updateContact = useCallback(
    async ({ contact }: IupdateContactActionProps) => {
      if (!state.contactSelected?.contact.id || !state.contactSelected.entity)
        return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateContactAction({
        dispatch,
        contactSelectedId: state.contactSelected.contact.id,
        contactEntityId: state.contactSelected.entity,
        contact,
        token,
        translate,
      });
    },
    [state.contactSelected?.contact.id, state.contactSelected?.entity]
  );

  const removeContact = useCallback(async ({ contactId }: IremoveContactActionProps) => {
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.removeContactAction({
      dispatch,
      contactId,
      token,
      translate,
    });
  }, []);

  const memoProvider = useMemo(
    () => ({
      ...state,
      getMainContacts,
      selectContact,
      createContact,
      updateContact,
      removeContact,
    }),
    [state, getMainContacts, selectContact, createContact, updateContact, removeContact]
  );

  return (
    <MainContactsContext.Provider value={memoProvider}>
      {props.children}
    </MainContactsContext.Provider>
  );
}

export default memo(MainContactsProvider);
